import React from 'react';
import { Stack } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { AccordionLoadingElement } from '~/domains/contest/domains/managev2/domains/common/fields/StaticField/Accordion/AccordionLoadingElement';

import { useSectionQuery } from '../../hooks/useSectionQuery';
import { BaseSectionComponent } from '../BaseSectionComponent/BaseSectionComponent';

import { SectionError } from './SectionError';
import { SectionSkeleton } from '../Sections/SectionSkeleton';

function LoadingAccordionShells({ sectionKey }: { sectionKey: string }) {
  const sectionConfig = {
    init: {
      title: 'Contest setup',
      accordions: [
        { key: 'selectTemplate', title: 'Select contest type', showLabels: true },
        { key: 'selectLeague', title: 'Select league', showLabels: true },
      ],
    },
    gameplay: {
      title: 'Gameplay',
      accordions: [
        { key: 'sportGameStyle', title: 'Sport & Game Style', showLabels: true },
        { key: 'schedule', title: 'Schedule', showLabels: true },
      ],
    },
    entries: {
      title: 'Entries settings',
      accordions: [
        { key: 'entryFeeSettings', title: 'Entry fee', showLabels: true },
        { key: 'contestSizeSettings', title: 'Contest size', showLabels: true },
      ],
    },
    contestDetails: {
      title: 'Contest details',
      accordions: [
        { key: 'general', title: 'General settings', showLabels: true },
        { key: 'advanced', title: 'Advanced settings', showLabels: true },
        { key: 'admin', title: 'Admin settings', showLabels: true, hideDuringLoading: true },
      ],
    },
  };

  const config = sectionConfig[sectionKey];
  if (!config) return null;

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={10} mt={12} mb={6}>
      {config.title && (
        <Text c="gray2.5" variant="subhead-small" transform="uppercase">
          {config.title}
        </Text>
      )}
      {config.accordions
        .filter((accordion) => !accordion.hideDuringLoading)
        .map((accordion) => (
          <AccordionLoadingElement
            key={accordion.key}
            title={accordion.title}
            showLoadingLabel={accordion.showLabels}
          />
        ))}
    </Stack>
  );
}

export function SectionSwitch({ sectionKey }: { sectionKey: string }) {
  const { sectionQuery, retryFn } = useSectionQuery(sectionKey);

  const createFlowSections = ['init', 'basicInfo', 'gameplay', 'entries', 'contestDetails'];
  const shouldShowSection = createFlowSections.includes(sectionKey);

  if (!shouldShowSection) {
    return null;
  }

  if (sectionQuery.error) {
    return <SectionError error={sectionQuery.error} sectionKey={sectionKey} onRetry={retryFn} />;
  }

  if (sectionQuery.isLoading) {
    if (sectionKey === 'basicInfo') {
      return <SectionSkeleton />;
    }

    if (sectionKey === 'init') {
      return <SectionSkeleton currentSectionKey={sectionKey} />;
    }

    return <LoadingAccordionShells sectionKey={sectionKey} />;
  }

  switch (sectionQuery.type) {
    case 'BaseSection': {
      return <BaseSectionComponent section={sectionQuery.data} />;
    }
    case 'DynamicSection': {
      return <BaseSectionComponent section={sectionQuery.data} />;
    }
    case 'DynamicSections': {
      return (
        <>
          {sectionQuery.data.map((sectionKey2) => (
            <SectionSwitch key={sectionKey2} sectionKey={sectionKey2} />
          ))}
        </>
      );
    }
    default: {
      return <Text>Unknown section type</Text>;
    }
  }
}
